import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AdminLayout from '../../../components/AdminLayout/AdminLayout';
import DashboardMainContainer from '../../../components/common/Containers/DashboardMainContainer';
import {
  AdminPageTitle,
  AdminSearchField,
  AdminTableHeader,
  AdminTableBody,
  AdminTableMeta,
  AdminStatusBadge,
  AdminRowAvatar,
  AdminRowMenuItem,
  AdminFixedRowActionsMenu,
  AdminRowActionsButton,
  AdminSupportingText,
  AdminListFilterDropdown,
  AdminRowMenuIcon,
  ADMIN_TOOLBAR_CLASS,
  ADMIN_TABLE_CLASS,
} from '../shared/adminUx';
import useClickOutside from '../../../Hooks/useClickOutside';
import useFixedRowActionsMenu, { ROW_ACTIONS_MENU_HEIGHT } from '../../../Hooks/useFixedRowActionsMenu';
import ProfilePlaceholder from '../../../assets/img/User.png';
import ConfigurationListPaginationFooter from '../../../components/AdminDashboard/Configuration/ConfigurationListPaginationFooter';
import { CONFIG_LIST_PAGE_SIZE } from '../../../utils/configurationTableUx';
import {
  ARCHIVE_FINAL_OUTCOME_OPTIONS,
  ARCHIVE_FINALISED_DATE_OPTIONS,
  COMPLIANCE_ARCHIVE_ROUTE,
  archiveListRow,
  collectArchiveCases,
  formatComplianceArchiveDateTime,
  getArchiveTypeFilterOptions,
  matchesArchiveFilters,
  matchesArchiveSearch,
} from '../../../data/complianceArchiveSpec';

const DEFAULT_FILTERS = {
  type: 'All',
  finalOutcome: 'All',
  dateRange: 'All',
};

/** Case ID · Business · Type · Name · Case Type · Final Outcome · Record Status · Decision ID · Finalised On · Actions */
const ARCHIVE_TABLE_GRID =
  'minmax(0, 1.05fr) minmax(0, 1.2fr) minmax(0, 0.65fr) minmax(0, 1.15fr) minmax(0, 0.85fr) minmax(0, 0.9fr) minmax(0, 0.85fr) minmax(0, 1fr) minmax(0, 0.95fr) minmax(62px, auto)';

const ComplianceArchive = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [items] = useState(() => collectArchiveCases().map(archiveListRow));
  const [filters, setFilters] = useState({ ...DEFAULT_FILTERS });
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [typeOpen, setTypeOpen] = useState(false);
  const [outcomeOpen, setOutcomeOpen] = useState(false);
  const [dateOpen, setDateOpen] = useState(false);
  const [rowActionsOpen, setRowActionsOpen] = useState({});

  const typeRef = useRef(null);
  const outcomeRef = useRef(null);
  const dateRef = useRef(null);
  const rowActionsRefs = useRef({});
  const tableScrollRef = useRef(null);
  const {
    layout: rowActionsMenuLayout,
    menuRef: rowActionsMenuRef,
    placementReady: rowActionsMenuPlacementReady,
  } = useFixedRowActionsMenu(
    rowActionsOpen,
    rowActionsRefs,
    tableScrollRef,
    ROW_ACTIONS_MENU_HEIGHT.SINGLE
  );

  const toggleRowActions = (rowKey) => {
    setRowActionsOpen((prev) => {
      if (prev[rowKey]) return {};
      return { [rowKey]: true };
    });
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      const isOutsideAll = Object.values(rowActionsRefs.current).every(
        (ref) => ref && !ref.contains(event.target)
      );
      if (isOutsideAll) setRowActionsOpen({});
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useClickOutside(typeRef, () => setTypeOpen(false));
  useClickOutside(outcomeRef, () => setOutcomeOpen(false));
  useClickOutside(dateRef, () => setDateOpen(false));

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 400);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, filters]);

  const filteredItems = useMemo(
    () =>
      items.filter(
        (row) => matchesArchiveSearch(row, debouncedSearch) && matchesArchiveFilters(row, filters)
      ),
    [items, debouncedSearch, filters]
  );

  const sortedItems = useMemo(() => {
    return [...filteredItems].sort((a, b) => {
      const ta = a.finalisedOn ? new Date(a.finalisedOn).getTime() : 0;
      const tb = b.finalisedOn ? new Date(b.finalisedOn).getTime() : 0;
      return tb - ta;
    });
  }, [filteredItems]);

  const totalCount = sortedItems.length;
  const totalPages = Math.max(1, Math.ceil(totalCount / CONFIG_LIST_PAGE_SIZE));
  const effectivePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    setCurrentPage((p) =>
      Math.min(p, Math.max(1, Math.ceil(sortedItems.length / CONFIG_LIST_PAGE_SIZE)))
    );
  }, [sortedItems.length]);

  const paginatedItems = useMemo(() => {
    const start = (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE;
    return sortedItems.slice(start, start + CONFIG_LIST_PAGE_SIZE);
  }, [sortedItems, effectivePage]);

  const goToRecord = (item, e) => {
    if (e) e.stopPropagation();
    navigate(`${COMPLIANCE_ARCHIVE_ROUTE}/${item.complianceCaseId || item.id}`, {
      state: {
        archiveItem: {
          ...item,
          complianceId: item.complianceCaseId,
          documentName: item.complianceItem,
          category: item.complianceType,
          finalStatus: item.finalOutcome,
          finalDecision: item.finalOutcome,
        },
        from: location.pathname,
      },
    });
  };

  const rangeStart = totalCount === 0 ? 0 : (effectivePage - 1) * CONFIG_LIST_PAGE_SIZE + 1;
  const rangeEnd = Math.min(effectivePage * CONFIG_LIST_PAGE_SIZE, totalCount);
  const showPagination = totalCount > CONFIG_LIST_PAGE_SIZE;

  return (
    <AdminLayout>
      <div>
        <AdminPageTitle>Compliance Archive</AdminPageTitle>

        <div className={ADMIN_TOOLBAR_CLASS}>
          <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center lg:justify-between">
            <AdminSearchField
              className="w-full max-w-[26.25rem]"
              type="text"
              placeholder="Search Case ID, Decision ID, Business, Licence, Badge or Ticket"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              aria-label="Search archived Compliance cases"
            />
            <div className="flex flex-wrap gap-3 items-center w-full lg:w-auto lg:justify-end">
              <AdminListFilterDropdown
                label="Finalised Date"
                value={filters.dateRange}
                options={ARCHIVE_FINALISED_DATE_OPTIONS}
                onChange={(value) => setFilters((prev) => ({ ...prev, dateRange: value }))}
                menuRef={dateRef}
                open={dateOpen}
                setOpen={setDateOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Final Outcome"
                value={filters.finalOutcome}
                options={ARCHIVE_FINAL_OUTCOME_OPTIONS}
                onChange={(value) => setFilters((prev) => ({ ...prev, finalOutcome: value }))}
                menuRef={outcomeRef}
                open={outcomeOpen}
                setOpen={setOutcomeOpen}
                size="lg"
              />
              <AdminListFilterDropdown
                label="Type"
                value={filters.type}
                options={getArchiveTypeFilterOptions()}
                onChange={(value) => setFilters((prev) => ({ ...prev, type: value }))}
                menuRef={typeRef}
                open={typeOpen}
                setOpen={setTypeOpen}
                size="lg"
              />
            </div>
          </div>
        </div>

        <DashboardMainContainer className={ADMIN_TABLE_CLASS}>
          <div ref={tableScrollRef} className="overflow-x-auto">
            <div className="min-w-0">
              <div
                className="grid py-3 text-offblack border-b border-lightgray px-4 gap-2"
                style={{ gridTemplateColumns: ARCHIVE_TABLE_GRID }}
              >
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Case ID</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Business</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Type</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Name</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Case Type</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Final Outcome</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Record Status</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Compliance Decision ID</AdminTableHeader>
                </div>
                <div className="flex items-center min-w-0">
                  <AdminTableHeader>Finalised On</AdminTableHeader>
                </div>
                <div className="flex items-center justify-center min-w-0">
                  <AdminTableHeader>Actions</AdminTableHeader>
                </div>
              </div>

              {paginatedItems.length === 0 ? (
                <div className="py-10 px-4 text-center">
                  <AdminSupportingText className="text-mediumgray">
                    No archive records found matching your filters.
                  </AdminSupportingText>
                </div>
              ) : (
                paginatedItems.map((item) => {
                  const archiveRowKey = item.complianceCaseId || item.id;
                  return (
                    <div
                      key={item.id}
                      className="grid py-3 px-4 text-offblack border-b border-lightgray last:border-b-0 gap-2 cursor-pointer hover:bg-pastalBlueWhite/50"
                      style={{ gridTemplateColumns: ARCHIVE_TABLE_GRID }}
                      onClick={(e) => goToRecord(item, e)}
                    >
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.complianceCaseId}>
                          {item.complianceCaseId}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center gap-2 min-w-0">
                        <AdminRowAvatar
                          src={item.businessAvatar || ProfilePlaceholder}
                          alt={item.businessName || ''}
                          onError={(e) => {
                            e.target.src = ProfilePlaceholder;
                          }}
                        />
                        <AdminTableBody emphasis className="truncate" title={item.businessName}>
                          {item.businessName}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.complianceType}>
                          {item.complianceType}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.complianceItem}>
                          {item.complianceItem}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.caseType}>
                          {item.caseType}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminStatusBadge status={item.finalOutcome} label={item.finalOutcome} />
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.recordStatus || '—'}>
                          {item.recordStatus || '—'}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableBody className="truncate" title={item.decisionId}>
                          {item.decisionId}
                        </AdminTableBody>
                      </div>
                      <div className="flex items-center min-w-0">
                        <AdminTableMeta
                          className="truncate"
                          title={formatComplianceArchiveDateTime(item.finalisedOn)}
                        >
                          {formatComplianceArchiveDateTime(item.finalisedOn)}
                        </AdminTableMeta>
                      </div>
                      <div
                        className="flex items-center justify-center relative"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div
                          className="relative inline-flex"
                          ref={(el) => {
                            rowActionsRefs.current[archiveRowKey] = el;
                          }}
                        >
                          <AdminRowActionsButton
                            onClick={(e) => {
                              e.stopPropagation();
                              toggleRowActions(archiveRowKey);
                            }}
                            title="Actions"
                            aria-label="Row actions"
                          >
                            <AdminRowMenuIcon />
                          </AdminRowActionsButton>
                          {rowActionsOpen[archiveRowKey] && rowActionsMenuLayout ? (
                            <AdminFixedRowActionsMenu
                              layout={rowActionsMenuLayout}
                              menuRef={rowActionsMenuRef}
                              placementReady={rowActionsMenuPlacementReady}
                            >
                              <AdminRowMenuItem
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setRowActionsOpen({});
                                  goToRecord(item, e);
                                }}
                              >
                                View
                              </AdminRowMenuItem>
                            </AdminFixedRowActionsMenu>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DashboardMainContainer>

        <ConfigurationListPaginationFooter
          show={showPagination}
          rangeStart={rangeStart}
          rangeEnd={rangeEnd}
          totalCount={totalCount}
          currentPage={effectivePage}
          totalPages={totalPages}
          onPrev={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
          onNext={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
          goToPage={setCurrentPage}
        />
      </div>
    </AdminLayout>
  );
};

export default ComplianceArchive;
